import { observer } from "mobx-react-lite"
import useStore from "reusable/mobx-store-utils"
import GapDialogBigTabsStore, { GapDialogBigTabsStoreProvider, useGapDialogBigTabsStore } from "./GapDialogBigTabsStore"
import { useEffect, useMemo } from "react"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import { themeConfig } from "@gap/shared/theme"
import Typography from "@mui/material/Typography"
import { OverridableComponent } from "@mui/material/OverridableComponent"
import { SvgIconTypeMap } from "@mui/material/SvgIcon"
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked"
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked"
import Divider from "@mui/material/Divider"
import { GapDialogAction } from "../GapDialogActionButton"
import { useGapDialogStore } from "../GapDialogStore"
import Fade from "@mui/material/Fade"

interface GapDialogBigTabProps {
   label: string
   endAdornment: OverridableComponent<SvgIconTypeMap<{}, "svg">>
   component: React.ReactNode
   buttons?: {
      primary?: GapDialogAction
      secondary?: GapDialogAction
      cancel?: GapDialogAction | boolean
   }
}

export interface GapDialogBigTabsProps {
   options: GapDialogBigTabProps[]
   /**
    * Label of the tab to show by default on first render. Falls back to the first option.
    */
   initialTab?: string
}

export default observer(function GapDialogBigTabs(props: GapDialogBigTabsProps) {
   const gapDialogStore = useGapDialogStore()
   const gapDialogBigTabsStore = useStore(sp => new GapDialogBigTabsStore(sp), { ...props, gapDialogStore })

   useEffect(() => {
      gapDialogBigTabsStore.initializeButtons()
   }, [gapDialogBigTabsStore])

   const Tabs = useMemo(() => {
      return props.options.map(option => <GapDialogBigTab key={option.label} {...option} />)
   }, [props.options])

   return <GapDialogBigTabsStoreProvider store={gapDialogBigTabsStore}>
      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'row' }}>
         {Tabs}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Fade in={true} key={gapDialogBigTabsStore.value} timeout={150}>
         <Box>
            {gapDialogBigTabsStore.Component}
         </Box>
      </Fade>
   </GapDialogBigTabsStoreProvider>
})

const GapDialogBigTab = observer(function GapDialogBigTab(props: GapDialogBigTabProps) {
   const gapDialogBigTabsStore = useGapDialogBigTabsStore()
   const CheckIcon = gapDialogBigTabsStore.isActive(props.label) ? RadioButtonChecked : RadioButtonUnchecked

   return <Box
      sx={{ borderRadius: '16px', border: `1px solid ${themeConfig.primary[200]}`, width: '49%', height: '75px', cursor: 'pointer' }}
      onClick={gapDialogBigTabsStore.clickHandler}
      data-id={props.label}
      data-testid={`message-dialog-tab-${props.label}`}
   >
      <Grid container sx={{ p: '24px 16px 24px 24px' }}>
         <Grid size={1}>
            <CheckIcon sx={{ height: '24px', width: '24px' }} />
         </Grid>
         <Grid sx={{ pl: 1 }} size={10}>
            <Typography variant="body1">{props.label}</Typography>
         </Grid>
         <Grid size={1}>
            <props.endAdornment sx={{ height: '24px', width: '24px' }} />
         </Grid>
      </Grid>
   </Box>
})
